import type { ContractTemplateFormInput, ContractTemplateView } from "@closer/shared";
import {
  CONTRACT_TEMPLATE_SYSTEM_LOCKED_MESSAGE,
  contractTemplateFormSchema,
} from "@closer/shared";
import {
  Button,
  Card,
  FormField,
  Input,
  PageHeader,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import {
  belgeyiMetneCevir,
  gonderilebilirBelge,
  kimlikUretici,
  metniMaddelereBol,
} from "../../lib/belgeMetni";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * ŞABLON DÜZENLEME (CLOS-126 `[108]`).
 *
 * ### ⚠ Sistem şablonu düzenlenmiyor ve ekran bunu SÖYLÜYOR
 *
 * Kart: *"sistem şablonları korunur."* Form hiç çizilmiyor; yerine sebebi ve kullanıcının
 * atabileceği adım (kendi kopyasını çıkarmak) yazıyor. Formu çizip sunucudan 403 almak,
 * kullanıcıya emeğini boşa harcatmak olurdu.
 *
 * ### ⚠ Gövde YALNIZ istenirse gönderiliyor
 *
 * Kullanıcı adı değiştirip metne dokunmadıysa istek metin taşımıyor: dokunulmayan alanı
 * yeniden yazmak, "kim neyi değiştirdi" sorusunu bulanıklaştırırdı.
 */

export function TemplateEditPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [metin, setMetin] = useState("");
  const [metneDokunuldu, setMetneDokunuldu] = useState(false);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "template", id),
    queryFn: async () =>
      (await api<ContractTemplateView>(`/api/templates/${id}`, { tenantId })).data,
  });

  const form = useZodForm(contractTemplateFormSchema);
  const sablon = sorgu.data;

  useEffect(() => {
    if (sablon) {
      form.reset({
        name: sablon.name,
        description: sablon.description ?? undefined,
        contractType: sablon.contractType ?? undefined,
      });
      /**
       * ⚠ MEVCUT METİN EKRANA GELİYOR (CLOS-126). Eskiden alan boş açılıyor ve altında
       * "metni değiştirmek istemiyorsanız boş bırakın" yazıyordu: kullanıcı kendi
       * şablonunu göremeden düzenliyordu, yani düzenlemek pratikte "baştan yaz" demekti.
       *
       * Metin YALNIZ ilk yüklemede basılıyor; `metneDokunuldu` sonrası sorgu tazelense
       * bile kullanıcının yazdığı kaybolmuyor.
       */
      if (!metneDokunuldu) setMetin(belgeyiMetneCevir(sablon.document ?? { clauses: [] }));
    }
  }, [sablon, form, metneDokunuldu]);

  const kaydet = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<ContractTemplateView>(`/api/templates/${id}`, {
        method: "PATCH",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "template", id) });
      /**
       * ⚠ SÜRÜM GEÇMİŞİ (CLOS-1117 · yetim uç G): her kayıt yeni bir sürüm satırı üretiyor;
       * detaydaki "Sürüm geçmişi" kartı 30 sn bayat kalmasın. Ön ek eşleşmesi zaten
       * kapsıyor; bağımlılık okunur olsun diye AÇIKÇA yazıldı.
       */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "template", id, "surumler"),
      });
      toast.success("Şablon güncellendi");
      void navigate(`/app/sablonlar/${id}`);
    },
    onError: (hata) => {
      /**
       * ⚠ Alan hatası FORMDA GÖRÜNMEYEBİLİR (kusur avı, 26 Ağustos): sunucu
       * `document.clauses.0.blocks.0.text` gibi bir yol döndürdüğünde `setError` formda
       * karşılığı olmayan bir alana yazıyor ve mesaj hiçbir yerde görünmüyordu.
       */
      if (hata instanceof ApiRequestError && hata.fields) {
        const formAlanlari = new Set(Object.keys(form.getValues()));
        const gorunmeyen = hata.fields.find((f) => !formAlanlari.has(f.field.split(".")[0]!));
        applyServerErrors(form, hata);
        if (gorunmeyen) toast.error(gorunmeyen.message);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Şablon güncellenemedi");
    },
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Şablon yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (sorgu.error || !sablon) {
    return (
      <StateView
        state={resolveErrorState(sorgu.error, { idempotent: true })}
        onRetry={() => void sorgu.refetch()}
      />
    );
  }

  if (sablon.isSystem) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={sablon.name} />
        <Card>
          <p className="text-sm text-fg">{CONTRACT_TEMPLATE_SYSTEM_LOCKED_MESSAGE}</p>
        </Card>
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sablonlar/${id}`}
        >
          Şablona dön
        </Link>
      </div>
    );
  }

  const gonder = form.handleSubmit((girdi: ContractTemplateFormInput) => {
    kaydet.mutate({
      name: girdi.name,
      description: girdi.description ?? null,
      contractType: girdi.contractType ?? null,
      /** ⚠ Metin YALNIZ dokunulduysa gidiyor. */
      ...(metneDokunuldu
        ? {
            /**
             * ⚠ Kimlikler MEVCUT şablondan devralınıyor (CLOS-633 ile aynı gerekçe):
             * yeniden üretilseydi düzenlenmeyen maddeler de değişmiş görünür ve şablon
             * revizyon geçmişi (`[108]`) olmayan bir farkı kaydederdi.
             */
            document: gonderilebilirBelge(
              metniMaddelereBol(metin, kimlikUretici(sablon.document ?? { clauses: [] })),
            ),
          }
        : {}),
    });
  });

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Şablonu düzenle"
        actions={
          <Link
            className="text-sm font-medium text-primary hover:underline"
            to={`/app/sablonlar/${id}`}
          >
            Vazgeç
          </Link>
        }
      />

      <Card padding="default">
        <form noValidate className="flex flex-col gap-3" onSubmit={gonder}>
          <FormField label="Şablon adı" error={form.formState.errors.name?.message} required>
            <Input {...form.register("name")} />
          </FormField>

          <FormField label="Açıklama" error={form.formState.errors.description?.message}>
            <Textarea rows={2} {...form.register("description")} />
          </FormField>

          {/*
            ⚠ DURUM BURADA DEĞİŞMİYOR (CLOS-128 `[110]`): arşivleme ayrı bir yetki
            (`template.archive`) ve kendi düğmesi şablon detayında. Aynı işlemi iki ekrandan
            yapılabilir kılmak, iki farklı yetkiyle aynı sonucu üretmek olurdu.
          */}
          {sablon.bodyKind === "document" ? (
            <FormField label="Şablon metni">
              <Textarea
                rows={18}
                value={metin}
                placeholder="Maddeleri boş satırla ayırın; her maddenin ilk satırı başlığı olur."
                onChange={(e) => {
                  setMetin(e.target.value);
                  setMetneDokunuldu(true);
                }}
              />
            </FormField>
          ) : (
            <p className="text-sm text-fg-soft">
              Bu şablon dosya tabanlı ({sablon.fileName}). Dosyayı değiştirmek için yeni bir şablon
              oluşturun.
            </p>
          )}

          <div className="flex justify-end">
            <Button type="submit" loading={kaydet.isPending}>
              Değişiklikleri kaydet
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/** `[106]` ile aynı sezgi: boş satır maddeyi ayırıyor, ilk satır başlık. */
